import "@tento-ui/components/hero-layout/hero-layout";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { WeatherPreview } from "@tento-ui/components/weather/weather-preview";
import { HeroLayout } from "@tento-ui/components/hero-layout/hero-layout";
import { SquircleMath } from "@tento-ui/components/squircle/squircle-math";
import { NaiveDate, YearMonth } from "@tento-chrono";
import { WeatherLocationChoice } from "../weather-location-choice";
import { LonaApi } from "../../api";
import { RowKey, RowNetworkKey } from "@tento-lona/sheets";
import { WeatherDay } from "../weather-day";

interface WeatherCellData {
  day: {
    avgtempF: number;
    avghumidity: number;
    uv: number;
    totalprecipIn: number;
    condition: { code: number; text: string };
  };
}

const HERO_LAYOUT_ROW_GAP = 2;
const PREVIEW_HEIGHT = 80;

@component({
  name: "std-weather-preview-card",
})
export class WeatherCardPreview extends LonaWebComponent {
  private selectedIndex: number = 0;
  private clipPathId: string = "";
  private $clipPath: SVGPathElement = this.hydrate("clip-path");
  private $heroLayout: HeroLayout = this.hydrate("hero-layout");
  private date: Option<NaiveDate>;

  onSizeChange = (s: Size) => {
    if (s.width === 0 || s.height === 0) return;

    const previewWidth = HeroLayout.calculatePreviewWidthFromHeight(
      s.width,
      s.height,
      PREVIEW_HEIGHT,
      HERO_LAYOUT_ROW_GAP,
    );

    this.$heroLayout.style.height = "100%";
    this.$heroLayout.bind({
      selectedIndex: this.selectedIndex,
      showNavBar: true,
      showScrollbar: false,
      previewSize: { width: previewWidth, height: PREVIEW_HEIGHT },
      onChange: (index: number) => {
        this.selectedIndex = index;
        this.onSizeChange?.(s);
      },
    });

    const radius = 38;
    const smoothing = 0.6;
    const { pathData, viewBox } = SquircleMath.genSvg(
      {
        width: s.width - 8,
        height: s.height - 8,
      },
      radius,
      smoothing,
    );

    const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(" ").map(Number);
    const scaleX = 1 / vbWidth;
    const scaleY = 1 / vbHeight;
    const translateX = -vbX * scaleX;
    const translateY = -vbY * scaleY;

    this.$clipPath.setAttribute("d", pathData);
    this.$clipPath.setAttribute(
      "transform",
      `translate(${translateX}, ${translateY}) scale(${scaleX}, ${scaleY})`,
    );
  };

  constructor() {
    super();
    this.clipPathId = `squircle-clip-${Math.random().toString(36).slice(2, 11)}`;
    this.$("clip-path-def").setAttribute("id", this.clipPathId);
    this.$heroLayout.style.clipPath = `url(#${this.clipPathId})`;
  }

  static makeWith(props: WeatherCardPreview.Props): WeatherCardPreview {
    const $component = WeatherCardPreview.make();
    $component.bind(props);
    return $component;
  }

  bind(props: WeatherCardPreview.Props): this {
    this.date = props.date;

    this.clearChildren();

    const date = this.date;
    // No location chosen: nothing to read, so no request and no cards.
    const rowKey = WeatherLocationChoice.previewRowKey(props);
    if (!date || rowKey == null) {
      return this;
    }

    const start = NaiveDate.fromYmd1Exp(date.yr, date.mth, 1);
    const dim = YearMonth.daysInMonth({ yr: date.yr, mth: date.mth });
    const end = start.addDays(dim);
    const fetchPromise = LonaApi.get("/api/rows/{rowKey}/cells", {
      path: { rowKey: RowNetworkKey.fromWire(rowKey) },
      query: {
        start,
        end,
        mock_sync_delay: null,
        booking_id: null,
      },
    });

    fetchPromise.then((cells) => {

      // A weather cell names its day as an ISO date string (`WeatherDay.dayOf`).
      const weatherData = WeatherDay.dayOf<WeatherCellData["day"]>(cells, date);

      const cards: {
        label: string;
        value: string;
      }[] = weatherData
        ? [
            {
              label: "Temperature",
              value: `${Math.round(weatherData.avgtempF)}°F`,
            },
            {
              label: "Humidity",
              value: `${weatherData.avghumidity}%`,
            },
            {
              label: "Condition",
              value: weatherData.condition.text,
            },
            {
              label: "UV",
              value: `${weatherData.uv}`,
            },
            {
              label: "Precipitation",
              value: `${weatherData.totalprecipIn} in`,
            },
          ]
        : [
            {
              label: "Tempature",
              value: "No Data",
            },
          ];

      this.clearChildren();

      for (const config of cards) {
        const $c = WeatherPreview.Container.makeWith();
        $c.assignStyles({
          "--padding": "4px",
        });

        const $p = WeatherPreview.make();
        $p.bind(config);
        $c.appendChild($p);

        const $item = HeroLayout.Item.makeWith({});
        $item.appendChild($c);
        this.$heroLayout.appendChild($item);
      }
    });

    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        position: relative;
        overflow: hidden;
        height: 100%;
      }

      #clip-svg {
        position: absolute;
        width: 0;
        height: 0;
        pointer-events: none;
      }

      #hero-layout {
        height: 100%;
        padding: 4px;
        background: var(--bg-surface-light);
        border: 2px solid var(--bg-hover-opaque);
        border-radius: 28px;
      }
    `,
  ];

  static $html = html`
    <svg id="clip-svg">
      <defs>
        <clipPath id="clip-path-def" clipPathUnits="objectBoundingBox">
          <path id="clip-path" />
        </clipPath>
      </defs>
    </svg>
    <std-hero-layout id="hero-layout"></std-hero-layout>
  `;
}

export namespace WeatherCardPreview {
  export type Props = {
    date: NaiveDate;
    lid?: Option<number>;
    rowKey?: Option<string>;
  };
}
